/**
 * canvas-engine/item-contract/itemConstants.js
 *
 * SINGLE SOURCE OF TRUTH for every shared rendering constant and per-type default
 * used by the Canva-style canvas builder (`admin-console-ui`) and the published-page
 * reader (`academy-ui`, via the synced `canvas-engine/` mirror). Authoring these
 * values once — and consuming them identically in both roles — is what structurally
 * prevents the builder and reader from drifting (design D1 / Requirement 3.5).
 *
 * ZERO-REUSE CONSTRAINT: this module imports NOTHING — not React, not the DOM, and
 * nothing from `admin-console-ui/src/modules/template-builder/` or
 * `academy-ui/src/template/`. It is pure data so it serializes, mirrors, and is
 * importable by the non-React Parity_Verifier.
 *
 * Every default below is transcribed EXACTLY from today's builder so already-saved
 * payloads render unchanged (Requirement 16.3). Sources:
 *   - MAX_GRID_COLS, SHADOW_PRESETS, BOX_DEFAULTS, BUTTON_FALLBACK, CONTENT_DEFAULTS,
 *     BACKGROUND_FALLBACK  → modules/canvas-builder/surface/ItemView.jsx
 *   - REFERENCE_WIDTH, ITEM_TYPES, DEFAULT_TEMPLATE_THEME → modules/canvas-builder/model/canvasModel.js
 *   - THEMEABLE → modules/canvas-builder/theme/themeEngine.js
 *
 * Requirements: 1.5, 1.7, 3.5, 6.1, 6.2, 7.6, 8.5, 9.5, 9.6, 14.1, 16.3, 17.3.
 */

// ── Schema + scaling constants ───────────────────────────────────────────────

/**
 * Schema version of the canva-style `section.canvas` payload (Req 17.3). The
 * Persistence_Fold writes this into every `section.canvas.schemaVersion`, and the
 * reader treats an absent/non-positive-integer version as this earliest version.
 */
export const CANVAS_SCHEMA_VERSION = 1;

/**
 * The fixed authoring/scaling width (pixels) at which all Item Geometry is defined
 * (Req 14.1). Matches the builder's `canvasModel.REFERENCE_WIDTH = 1180`; persisted
 * verbatim into `section.canvas.referenceWidth` and used as-is by the reader's
 * Scale_Factor.
 */
export const REFERENCE_WIDTH = 1180;

// ── Shared render constants ──────────────────────────────────────────────────

/**
 * The single Image_Grid column cap (Req 3.5). Transcribed from `ItemView`'s
 * `MAX_GRID_COLS = 6` (the reader previously used a divergent `3`).
 */
export const MAX_GRID_COLS = 6;

/**
 * Named box-shadow presets selectable per Item (Req 3.5). Keys are the values stored
 * in `item.style.boxShadow`; the mapped string is the CSS `box-shadow` declaration.
 * Transcribed verbatim from `ItemView`'s `SHADOWS` map (includes `none`).
 */
export const SHADOW_PRESETS = {
  none: 'none',
  sm: '0 1px 3px rgba(15,23,42,0.16)',
  md: '0 6px 18px rgba(15,23,42,0.18)',
  lg: '0 18px 50px rgba(15,23,42,0.28)',
};

// ── Theme constants ──────────────────────────────────────────────────────────

/**
 * The themeable property set resolved through the Theme_Cascade (Req 11.6).
 * Transcribed from `themeEngine.THEMEABLE`.
 */
export const THEMEABLE = ['textColor', 'backgroundColor', 'fontFamily'];

/**
 * The always-present base of the Theme_Cascade when `template.theme` is missing,
 * empty, or omits a property (Req 9.5 / 9.6). Transcribed from
 * `canvasModel.DEFAULT_TEMPLATE_THEME`.
 */
export const DEFAULT_TEMPLATE_THEME = {
  textColor: '#1A1A1A',
  backgroundColor: '#FFFFFF',
  fontFamily: 'Inter, sans-serif',
};

// ── Item types ───────────────────────────────────────────────────────────────

/** The six supported Item types. Transcribed from `canvasModel.ITEM_TYPES`. */
export const ITEM_TYPES = ['text', 'image', 'grid', 'button', 'container', 'paper'];

// ── Per-type box-style defaults (Req 6.1, 6.2, 7.6, 16.3) ─────────────────────

/**
 * Per-type defaults for the box wrapper style, transcribed EXACTLY from `ItemView`'s
 * inline computation:
 *   radius      = num(st.borderRadius, isPaper ? 12 : type === 'button' ? 8 : 0)
 *   borderWidth = num(st.borderWidth,  isContainer ? 1 : 0)
 *   borderColor = st.borderColor || (isContainer ? '#cbd5e1' : '#e5e7eb')
 *   padding     = num(st.padding,      isPaper || isContainer ? 16 : 0)
 *   boxShadow   = SHADOW_PRESETS[st.boxShadow] || (isPaper ? 'md' : 'none')  // key
 *   opacity     = num(st.opacity, 1)
 *
 * `boxShadow` holds the SHADOW_PRESETS *key* (not the resolved CSS string) so the
 * default is expressed in the same vocabulary as the stored override.
 */
export const BOX_DEFAULTS = {
  text: { borderRadius: 0, borderWidth: 0, borderColor: '#e5e7eb', padding: 0, boxShadow: 'none', opacity: 1 },
  image: { borderRadius: 0, borderWidth: 0, borderColor: '#e5e7eb', padding: 0, boxShadow: 'none', opacity: 1 },
  grid: { borderRadius: 0, borderWidth: 0, borderColor: '#e5e7eb', padding: 0, boxShadow: 'none', opacity: 1 },
  button: { borderRadius: 8, borderWidth: 0, borderColor: '#e5e7eb', padding: 0, boxShadow: 'none', opacity: 1 },
  container: { borderRadius: 0, borderWidth: 1, borderColor: '#cbd5e1', padding: 16, boxShadow: 'none', opacity: 1 },
  paper: { borderRadius: 12, borderWidth: 0, borderColor: '#e5e7eb', padding: 16, boxShadow: 'md', opacity: 1 },
};

/**
 * Button fill/foreground fallback applied when the Theme_Cascade leaves a button's
 * fill/foreground undefined (Req 8.5). Transcribed from `ItemView`:
 *   fill = resolved.backgroundColor || '#2563eb'
 *   fg   = resolved.textColor || (variant === 'filled' ? '#ffffff' : fill)
 * `filledFg` is the foreground for the default `filled` variant; `outline`/`text`
 * variants fall back to the `fill` color (handled by the Render_Contract).
 */
export const BUTTON_FALLBACK = { fill: '#2563eb', filledFg: '#ffffff' };

// ── Per-type background fallbacks (Req 6.1, 6.2) ──────────────────────────────

/**
 * Per-type background-color fallback applied AFTER the Theme_Cascade resolves no
 * `backgroundColor`, transcribed from `ItemView`'s box `backgroundColor` computation:
 *   paper     → resolved.backgroundColor || '#ffffff'
 *   container → resolved.backgroundColor || 'transparent'
 *   text      → resolved.backgroundColor (no fallback)
 *   others    → undefined (no background)
 * `undefined` entries mean "apply no background when the cascade yields none".
 */
export const BACKGROUND_FALLBACK = {
  text: undefined,
  image: undefined,
  grid: undefined,
  button: undefined,
  container: 'transparent',
  paper: '#ffffff',
};

// ── Per-type inner-content defaults (Req 7.6, 16.3) ───────────────────────────

/**
 * Per-type defaults for the inner-content style options, transcribed EXACTLY from
 * `ItemView`'s per-type render branches:
 *   text   → fontSize num(st.fontSize, 16), fontWeight st.fontWeight || 400,
 *            textAlign st.textAlign || 'left', lineHeight num(st.lineHeight, 1.4)
 *   image  → objectFit st.objectFit || 'cover'
 *   grid   → objectFit st.objectFit || 'cover', gridGap num(st.gridGap, 4);
 *            columns default = clamp(ceil(sqrt(cellCount)), 1, MAX_GRID_COLS)
 *   button → fontSize num(st.fontSize, 14), fontWeight st.fontWeight || 600,
 *            buttonVariant st.buttonVariant || 'filled',
 *            paddingFallback '0 12px' (used when the box padding resolves falsy)
 */
export const CONTENT_DEFAULTS = {
  text: { fontSize: 16, fontWeight: 400, textAlign: 'left', lineHeight: 1.4 },
  image: { objectFit: 'cover' },
  grid: { objectFit: 'cover', gridGap: 4 },
  button: { fontSize: 14, fontWeight: 600, buttonVariant: 'filled', paddingFallback: '0 12px' },
};
